import React from 'react';
import { useNavigate } from 'react-router-dom';
import PublicNav from './PublicNav';
import PublicFooter from './PublicFooter';
import './HowItWorksPage.css';

const HowItWorksPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="how-it-works-page">
      <PublicNav onLoginClick={() => navigate('/login')} />
      
      <div className="how-it-works-container">
        <header className="how-it-works-header">
          <h1>How It Works</h1>
          <p className="how-it-works-subtitle">Four steps to digitize and manage asset ownership</p>
        </header>

        <section className="steps-section">
          <div className="step-card step-card-1">
            <div className="step-visual">
              <div className="step-circle">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                </svg>
              </div>
              <div className="step-arrow">→</div>
            </div>
            <div className="step-content-wrapper">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Create or Access Assets</h3>
                <p className="step-intro">What you see depends on your role. Asset creators build. Owners view. Both experiences are streamlined.</p>
                <div className="step-details">
                  <div className="step-detail-item">
                    <strong>For Asset Creators</strong>
                    <p>Create a new asset by giving it a name (like "Acme Corp Shares" or "Property Fund Units"). Define the total number of ownership units. The platform deploys a smart contract to the blockchain—this becomes your asset's permanent, immutable record.</p>
                  </div>
                  <div className="step-detail-item">
                    <strong>For Owners & Investors</strong>
                    <p>Once you're invited to an asset, it appears in your dashboard. You can see all assets where you have ownership, view your current holdings, and track your equity across multiple companies, properties, or funds.</p>
                  </div>
                  <div className="step-detail-item">
                    <strong>The Blockchain Connection</strong>
                    <p>Each asset gets its own smart contract on the blockchain. This contract holds the ownership records—who owns how many units. The platform links this on-chain data to your account, making it easy to view and manage.</p>
                  </div>
                </div>
                <p className="step-note">Whether you're creating assets or viewing your portfolio, everything is organized in one clear dashboard. No need to jump between spreadsheets or multiple systems.</p>
              </div>
            </div>
          </div>

          <div className="step-card step-card-2">
            <div className="step-visual">
              <div className="step-circle">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                </svg>
              </div>
              <div className="step-arrow">→</div>
            </div>
            <div className="step-content-wrapper">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Manage Ownership</h3>
                <p className="step-intro">This is where ownership gets allocated, vesting schedules are set, and wallets are connected. The process is straightforward but powerful.</p>
                <div className="step-details">
                  <div className="step-detail-item">
                    <strong>Allocate Ownership Units</strong>
                    <p>Asset creators invite owners by email. Once an owner accepts and links their wallet, creators can allocate ownership units. These units are minted on the blockchain and assigned to the owner's wallet address. The allocation is permanent and transparent—anyone can verify it on-chain.</p>
                  </div>
                  <div className="step-detail-item">
                    <strong>Set Up Vesting Schedules</strong>
                    <p>For employee equity or time-based grants, creators define vesting schedules: total units, start date, cliff period (if any), and vesting duration. The platform calculates vested amounts automatically over time. Owners see their vested vs unvested units in real-time.</p>
                  </div>
                  <div className="step-detail-item">
                    <strong>Connect Your Wallet</strong>
                    <p>Owners connect their Ethereum-compatible wallet (like MetaMask) to view their on-chain holdings. The platform links your wallet address to your account, so you can see both on-chain balances and off-chain details (like vesting schedules) in one place.</p>
                  </div>
                  <div className="step-detail-item">
                    <strong>Whitelist Management</strong>
                    <p>Only whitelisted wallet addresses can hold tokens. Asset creators manage the whitelist, adding addresses as owners join and removing them if needed. This ensures only approved parties can participate.</p>
                  </div>
                </div>
                <p className="step-note">All ownership changes are recorded on the blockchain, providing a permanent audit trail. The platform makes it easy to manage, but the blockchain ensures it's trustworthy.</p>
              </div>
            </div>
          </div>

          <div className="step-card step-card-3">
            <div className="step-visual">
              <div className="step-circle">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </div>
              <div className="step-arrow">→</div>
            </div>
            <div className="step-content-wrapper">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Exchange Ownership</h3>
                <p className="step-intro">Ownership units can be transferred between whitelisted addresses, enabling exchanges for other cryptocurrencies or fiat currency through approved channels.</p>
                <div className="step-details">
                  <div className="step-detail-item">
                    <strong>Transfer Between Owners</strong>
                    <p>When transfers are enabled by the asset creator, owners can transfer their ownership units to other whitelisted wallet addresses. This happens directly on the blockchain—the smart contract verifies both parties are whitelisted and records the transfer permanently.</p>
                  </div>
                  <div className="step-detail-item">
                    <strong>Exchange for Cryptocurrency</strong>
                    <p>Owners can exchange their ownership units for other cryptocurrencies (like ETH, USDC, or stablecoins) through approved exchanges or OTC (over-the-counter) platforms. The exchange process involves transferring ownership tokens to the buyer's whitelisted wallet in exchange for cryptocurrency.</p>
                  </div>
                  <div className="step-detail-item">
                    <strong>Exchange for Fiat Currency</strong>
                    <p>Ownership can also be exchanged for traditional fiat currency (USD, EUR, etc.) through regulated exchanges or direct transactions. The buyer receives the ownership tokens in their wallet, while the seller receives fiat payment through bank transfer or other agreed-upon methods. The platform tracks these exchanges for compliance and record-keeping.</p>
                  </div>
                  <div className="step-detail-item">
                    <strong>Transfer Restrictions & Control</strong>
                    <p>Asset creators maintain control over transfers. They can enable or disable transfers at any time, ensuring compliance with regulations and company policies. All transfers require both parties to be whitelisted, preventing unauthorized transactions. The blockchain provides a complete, immutable record of all ownership changes.</p>
                  </div>
                </div>
                <p className="step-note">While ownership units can be exchanged, these are private assets—not publicly traded securities. All transfers must comply with applicable regulations, and asset creators can restrict or enable transfers as needed for compliance and control.</p>
              </div>
            </div>
          </div>

          <div className="step-card step-card-4">
            <div className="step-visual">
              <div className="step-circle">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                </svg>
              </div>
            </div>
            <div className="step-content-wrapper">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Track & Distribute</h3>
                <p className="step-intro">Once everything is set up, the platform handles the ongoing work automatically. Vesting tracks itself. Distributions calculate themselves. You just monitor and manage.</p>
                <div className="step-details">
                  <div className="step-detail-item">
                    <strong>Automatic Vesting Tracking</strong>
                    <p>The platform continuously calculates how many units have vested based on your grant schedules. Owners see their progress in real-time: vested units, unvested units, and when the next vesting milestone occurs. No manual calculations or spreadsheet updates needed.</p>
                  </div>
                  <div className="step-detail-item">
                    <strong>Distribution Calculations</strong>
                    <p>When asset creators declare a distribution (like a dividend or profit share), the platform takes a snapshot of ownership on the record date. It then calculates each owner's pro-rata entitlement automatically based on their ownership percentage. Everyone sees exactly what they're owed.</p>
                  </div>
                  <div className="step-detail-item">
                    <strong>Payment Tracking</strong>
                    <p>After distributions are calculated, creators mark payments as they're made (off-chain settlement via bank transfer, check, etc.). Owners can see the status: what's been declared, what they're owed, and what's been paid. Everything is transparent and auditable.</p>
                  </div>
                  <div className="step-detail-item">
                    <strong>Real-Time Updates</strong>
                    <p>All changes reflect immediately. When new units are allocated, vesting progresses, or distributions are declared, the dashboard updates in real-time. Owners always have the latest view of their holdings and entitlements.</p>
                  </div>
                </div>
                <p className="step-note">The platform handles the complexity so you don't have to. Ownership records are immutable on the blockchain, calculations are automatic, and everything is transparent. You can focus on what matters—managing your assets or tracking your equity.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="details-section">
          <h2 className="details-title">Who Benefits?</h2>
          <div className="detail-grid">
            <div className="detail-card detail-card-creator">
              <div className="detail-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                </svg>
              </div>
              <h3>Asset Creators</h3>
              <p>Create your asset, invite owners, set up vesting schedules, and manage distributions—all in one place.</p>
            </div>

            <div className="detail-card detail-card-owner">
              <div className="detail-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>
              <h3>Owners & Investors</h3>
              <p>View your ownership percentage, track vesting progress, and see your distribution entitlements in real-time.</p>
            </div>

            <div className="detail-card detail-card-blockchain">
              <div className="detail-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                </svg>
              </div>
              <h3>Blockchain Powered</h3>
              <p>Ownership is recorded on the blockchain for transparency and immutability, while maintaining privacy for identities.</p>
            </div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
};

export default HowItWorksPage;

